import { useEffect, useState } from 'react';
import { businessSectorService, BusinessSectorTableResponse } from '../../services/business-sector-table';
import { getBusinessPlansTableData } from '../../services/commerce.service';
import {
  masterManualComisionHeadings,
  masterPersonalizedComisionHeadings,
  standarComisionHeadings,
  visaManualComisionHeadings,
  visaPersonalizedComisionHeadings,
} from '../../utils/data';
import {
  formatTableDataForTariffsMaster,
  formatTableDataForTariffsVisa,
  getEnumTypeValue,
} from '../../utils/helpers';
import { TariffType } from '../../types/enums/tariff.enum';
import CustomTable from '../CustomTable/CustomTable';

// Formats a tariff value to the app-wide convention: two decimals, comma decimal
// separator and a single trailing '%' (e.g. '1.05%' / 1.05 / '1,05' -> '1,05%').
const formatArancel = (value: string | number | undefined | null) => {
  if (value === undefined || value === null || value === '') return '-';
  const numeric = String(value).replace('%', '').replace(',', '.').trim();
  if (numeric === '' || Number.isNaN(Number(numeric))) return '-';
  return `${Number(numeric).toFixed(2).replace('.', ',')}%`;
};

const getTariffTypeLabel = (isStandard?: boolean, isManual?: boolean) => {
  if (isStandard) return 'Estándar';
  if (isManual) return 'Manual';
  return 'Personalizado';
};

interface TariffBlockProps {
  isStandarTariff?: boolean;
  tariffType?: TariffType | null;
  customTariff?: any;
  businessTaxPayerType: any;
  businessCategory: any;
  acceptVisa: boolean;
  acceptMaster: boolean;
  currency?: string;
  anticipatePayments?: boolean;
}

function TariffBlock({
  isStandarTariff,
  tariffType,
  customTariff,
  businessTaxPayerType,
  businessCategory,
  acceptVisa,
  acceptMaster,
  currency,
  anticipatePayments,
}: TariffBlockProps) {
  const [tableDataVisa, setTableDataVisa] = useState<any[]>([]);
  const [tableDataMaster, setTableDataMaster] = useState<any[]>([]);
  const [sectorData, setSectorData] = useState<BusinessSectorTableResponse | null>(null);

  useEffect(() => {
    const getTableData = async () => {
      const response = await getBusinessPlansTableData(
        getEnumTypeValue('taxPayer', businessTaxPayerType),
        getEnumTypeValue('businessCategory', businessCategory),
      );

      if (response.result) {
        const formattedTableDataVisa = formatTableDataForTariffsVisa(response.data);
        setTableDataVisa(formattedTableDataVisa);

        const formattedTableDataMaster = formatTableDataForTariffsMaster(response.data);
        setTableDataMaster(formattedTableDataMaster);
      }
    };

    void getTableData();
  }, []);

  // For personalized tariffs, the suggested ('Arancel sugerido') and minimum
  // ('Arancel mínimo') columns must come from the business-sector table per brand,
  // not from hardcoded placeholders. Fetched only when the tariff is personalized.
  useEffect(() => {
    if (isStandarTariff || !businessCategory?.name) return;

    businessSectorService
      .getBusinessSectorTable(businessCategory.name)
      .then(setSectorData)
      .catch(() => setSectorData(null));
  }, [isStandarTariff, businessCategory?.name]);

  const PLAN_ORDER = [
    'DÉBITO',
    'PREPAGO',
    'CRÉDITO CONTADO',
    'CRÉDITO CUOTAS',
    'Extranjeros DÉBITO',
    'Extranjeros CRÉDITO',
  ];

  const sectorSource = customTariff?.exceedsAnnualBilling ? sectorData?.exceeds : sectorData?.standard;
  const masterSectorRows = sectorSource?.master?.[0]?.rows ?? [];
  const visaSectorRows = sectorSource?.visa?.[0]?.rows ?? [];

  const masterPersonalizedByPlan: Record<string, any> = {
    DÉBITO: customTariff?.masterDebit,
    PREPAGO: customTariff?.masterPrepaid,
    'CRÉDITO CONTADO': customTariff?.masterCashCredit,
    'CRÉDITO CUOTAS': customTariff?.masterInstallmentCredit,
    'Extranjeros DÉBITO': customTariff?.masterForeignDebit,
    'Extranjeros CRÉDITO': customTariff?.masterForeignCredit,
  };

  const visaPersonalizedByPlan: Record<string, any> = {
    DÉBITO: customTariff?.visaDebit,
    PREPAGO: customTariff?.visaPrepaid,
    'CRÉDITO CONTADO': customTariff?.visaCashCredit,
    'CRÉDITO CUOTAS': customTariff?.visaInstallmentCredit,
    'Extranjeros DÉBITO': customTariff?.visaForeignDebit,
    'Extranjeros CRÉDITO': customTariff?.visaForeignCredit,
  };

  const buildPersonalizedRows = (sectorRows: any[], personalizedByPlan: Record<string, any>) =>
    PLAN_ORDER.map((plan) => {
      const sectorRow = sectorRows.find((row) => row.plan === plan);
      return {
        plan,
        currency: formatArancel(personalizedByPlan[plan]),
        fees: formatArancel(sectorRow?.fees),
        deferred: formatArancel(sectorRow?.deferred),
      };
    });

  const masterData = [{ rows: buildPersonalizedRows(masterSectorRows, masterPersonalizedByPlan) }];
  const visaData = [{ rows: buildPersonalizedRows(visaSectorRows, visaPersonalizedByPlan) }];

  if (!isStandarTariff && !customTariff) return null;

  const isManualTariff = !isStandarTariff && tariffType === TariffType.MANUAL;
  const tariffTypeLabel = getTariffTypeLabel(isStandarTariff, isManualTariff);

  return (
    <div className="confirmation-card">
      <p className="confirmation-title">{'CONDICIONES Y TARIFARIO'.toUpperCase()}</p>
      <div className="confirmation-info">
        <div className="info-fieldname">Moneda</div>
        <div className="info semibold">{currency ?? '-'}</div>
      </div>
      <div className="confirmation-info">
        <div className="info-fieldname">Anticipar pagos</div>
        <div className="info semibold">{anticipatePayments ? 'Sí' : 'No'}</div>
      </div>
      <div className="confirmation-info">
        <div className="info-fieldname">Tipo de tarifario</div>
        <div className="info semibold">{tariffTypeLabel}</div>
      </div>
      <div className="confirmation-info">
        <div className="info-fieldname">Facturación anual mayor a USD 250.000</div>
        <div className="info semibold">{customTariff?.exceedsAnnualBilling ? 'Sí' : 'No'}</div>
      </div>
      {isStandarTariff ? (
        <>
          {acceptVisa && (
            <>
              <p className="processor-title">VISA</p>
              <CustomTable tableData={tableDataVisa} headings={standarComisionHeadings} />
            </>
          )}
          {acceptMaster && (
            <>
              <p className="processor-title">MASTER</p>
              <CustomTable tableData={tableDataMaster} headings={standarComisionHeadings} />
            </>
          )}
        </>
      ) : (
        <>
          <div className="table-wrapper">
            <>
              <p className="confirmation-title">Propuesta MASTER</p>
              <CustomTable
                tableData={masterData}
                headings={isManualTariff ? masterManualComisionHeadings : masterPersonalizedComisionHeadings}
              />
            </>
            <>
              <p className="confirmation-title">Propuesta VISA</p>
              <CustomTable
                tableData={visaData}
                headings={isManualTariff ? visaManualComisionHeadings : visaPersonalizedComisionHeadings}
              />
            </>
          </div>
        </>
      )}
    </div>
  );
}

export default TariffBlock;
